// XP and Leveling System for CommandLearn

// Level thresholds - XP required to reach each level
const LEVEL_THRESHOLDS = [
  0,      // Level 1: Novice (starting level)
  100,    // Level 2: Apprentice
  300,    // Level 3: Practitioner
  600,    // Level 4: Journeyman
  1000,   // Level 5: Expert
  1500,   // Level 6: Master
  2200,   // Level 7: Grandmaster
  3000    // Level 8: Legend
];

const LEVEL_TITLES = [
  'Novice',       // Level 1
  'Apprentice',   // Level 2
  'Practitioner', // Level 3
  'Journeyman',   // Level 4
  'Expert',       // Level 5
  'Master',       // Level 6
  'Grandmaster',  // Level 7
  'Legend'        // Level 8
];

// Base XP rewards by difficulty
const BASE_XP = {
  beginner: 10,
  intermediate: 25,
  advanced: 50,
  expert: 100
};

// Streak multiplier thresholds
const STREAK_MULTIPLIERS = [
  { min: 0, max: 2, multiplier: 1.0 },
  { min: 3, max: 5, multiplier: 1.5 },
  { min: 6, max: 9, multiplier: 2.0 },
  { min: 10, max: Infinity, multiplier: 3.0 }
];

/**
 * Calculate the current level based on total XP
 * @param {number} totalXP - Total XP accumulated
 * @returns {number} Current level (1-8)
 */
export const calculateLevel = (totalXP) => {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVEL_THRESHOLDS[i]) {
      return i + 1;
    }
  }
  return 1;
};

/**
 * Get the title for a given level
 * @param {number} level - Level number (1-8)
 * @returns {string} Level title
 */
export const getLevelTitle = (level) => {
  return LEVEL_TITLES[Math.min(level - 1, LEVEL_TITLES.length - 1)] || 'Novice';
};

/**
 * Get XP required for the next level
 * @param {number} totalXP - Current total XP
 * @returns {object} { current, required, percentage }
 */
export const getXPForNextLevel = (totalXP) => {
  const currentLevel = calculateLevel(totalXP);

  if (currentLevel >= LEVEL_THRESHOLDS.length) {
    // Max level reached
    return {
      current: totalXP,
      required: totalXP,
      percentage: 100,
      isMaxLevel: true
    };
  }

  const currentLevelThreshold = LEVEL_THRESHOLDS[currentLevel - 1];
  const nextLevelThreshold = LEVEL_THRESHOLDS[currentLevel];
  const xpInCurrentLevel = totalXP - currentLevelThreshold;
  const xpRequiredForLevel = nextLevelThreshold - currentLevelThreshold;

  return {
    current: xpInCurrentLevel,
    required: xpRequiredForLevel,
    percentage: Math.round((xpInCurrentLevel / xpRequiredForLevel) * 100),
    isMaxLevel: false
  };
};

/**
 * Get the streak multiplier based on current streak
 * @param {number} streak - Current streak count
 * @returns {number} Multiplier value
 */
export const getStreakMultiplier = (streak) => {
  for (const tier of STREAK_MULTIPLIERS) {
    if (streak >= tier.min && streak <= tier.max) {
      return tier.multiplier;
    }
  }
  return 1.0;
};

/**
 * Calculate XP reward for completing a lesson
 * @param {string} difficulty - Lesson difficulty (beginner, intermediate, advanced, expert)
 * @param {number} streak - Current streak count
 * @param {object} options - Additional options
 * @param {boolean} options.usedHint - Whether hint was used
 * @param {boolean} options.usedAnswer - Whether answer was revealed
 * @param {number} options.timeInSeconds - Time taken to answer
 * @returns {number} XP reward
 */
export const calculateXPReward = (difficulty, streak, options = {}) => {
  const { usedHint = false, usedAnswer = false, timeInSeconds = null } = options;

  // Base XP for difficulty
  let baseXP = BASE_XP[difficulty] || BASE_XP.beginner;

  // Apply streak multiplier
  const streakMultiplier = getStreakMultiplier(streak);
  let xp = baseXP * streakMultiplier;

  // Speed bonus: +50% if answered in under 10 seconds
  if (timeInSeconds !== null && timeInSeconds < 10) {
    xp *= 1.5;
  }

  // First attempt bonus: +25% if no hints or answer revealed
  if (!usedHint && !usedAnswer) {
    xp *= 1.25;
  }

  // Penalty for using answer reveal
  if (usedAnswer) {
    xp *= 0.25; // Only 25% XP if answer was revealed
  } else if (usedHint) {
    xp *= 0.75; // 75% XP if hint was used
  }

  return Math.round(xp);
};

/**
 * Get streak tier information
 * @param {number} streak - Current streak
 * @returns {object} { tier, multiplier, nextTier, streaksUntilNext }
 */
export const getStreakTierInfo = (streak) => {
  let currentTier = 0;
  let multiplier = 1.0;

  for (let i = 0; i < STREAK_MULTIPLIERS.length; i++) {
    const tier = STREAK_MULTIPLIERS[i];
    if (streak >= tier.min && streak <= tier.max) {
      currentTier = i;
      multiplier = tier.multiplier;
      break;
    }
  }

  const nextTierIndex = currentTier + 1;
  const hasNextTier = nextTierIndex < STREAK_MULTIPLIERS.length;
  const nextTier = hasNextTier ? STREAK_MULTIPLIERS[nextTierIndex] : null;
  const streaksUntilNext = hasNextTier ? nextTier.min - streak : 0;

  return {
    tier: currentTier,
    multiplier,
    nextTier: hasNextTier ? nextTier.multiplier : null,
    streaksUntilNext
  };
};

/**
 * Format XP number with comma separators
 * @param {number} xp - XP value
 * @returns {string} Formatted string
 */
export const formatXP = (xp) => {
  return xp.toLocaleString();
};

/**
 * Get all level information
 * @returns {Array} Array of level objects with threshold and title
 */
export const getAllLevels = () => {
  return LEVEL_THRESHOLDS.map((threshold, index) => ({
    level: index + 1,
    threshold,
    title: LEVEL_TITLES[index]
  }));
};

export default {
  calculateLevel,
  getLevelTitle,
  getXPForNextLevel,
  getStreakMultiplier,
  calculateXPReward,
  getStreakTierInfo,
  formatXP,
  getAllLevels,
  BASE_XP,
  LEVEL_THRESHOLDS,
  LEVEL_TITLES
};
